import type { ComponentPropsWithoutRef, ReactNode } from "react";
import * as Switch from "@radix-ui/react-switch";

interface ToggleButtonProps extends Omit<
  ComponentPropsWithoutRef<typeof Switch.Root>,
  "checked" | "onCheckedChange" | "onToggle"
> {
  checked: boolean;
  onToggle: (checked: boolean) => void;
  inactiveColor?: string;
  inactiveHoverColor?: string;
}

export const ToggleButton = ({
  checked,
  onToggle,
  inactiveColor = "var(--color-toggle-inactive)",
  inactiveHoverColor: _inactiveHoverColor,
  className = "",
  ...props
}: ToggleButtonProps) => {
  void _inactiveHoverColor; // reservado para futuro hover con CSS
  return (
    <Switch.Root
      checked={checked}
      onCheckedChange={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus-global-ring shrink-0 data-[state=checked]:bg-(--btn-primary) data-[state=unchecked]:border data-[state=unchecked]:border-(--border-secondary) ${className}`.trim()}
      style={
        checked
          ? undefined
          : {
              backgroundColor: inactiveColor,
            }
      }
      {...props}
    >
      <Switch.Thumb className="block h-4 w-4 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-1" />
    </Switch.Root>
  );
};

interface ToggleSwitchProps extends Omit<
  ComponentPropsWithoutRef<typeof Switch.Root>,
  "checked" | "onCheckedChange" | "onToggle" | "children"
> {
  checked: boolean;
  onToggle: (checked: boolean) => void;
  label?: string;
  id?: string;
  className?: string;
  children?: ReactNode;
}

export const ToggleSwitch = ({
  checked,
  onToggle,
  label,
  id,
  className = "",
  children,
  ...props
}: ToggleSwitchProps) => {
  const displayLabel = label ?? "";

  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 bg-global-overlay-secondary rounded-lg border border-global-secondary ${className}`.trim()}
    >
      {displayLabel && (
        <label
          htmlFor={id}
          className="text-size-xs font-medium text-global-label cursor-pointer flex-1 inline-flex items-center gap-1.5"
        >
          {displayLabel}
          {children}
        </label>
      )}
      <ToggleButton checked={checked} onToggle={onToggle} id={id} {...props} />
    </div>
  );
};
